'use client';

import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { useNavAccess } from '@/hooks/useNavAccess';
import { useAuthStore } from '@/stores/auth.store';
import apiClient from '@/lib/api/client';
import { AUTH_ENDPOINTS } from '@/constants/api';
import { ROLE_LABELS } from '@/constants/auth';

// ── Theme Context ──────────────────────────────────────────────────────────────
const ThemeContext = createContext<{ dark: boolean; toggle: () => void }>({
  dark: true,
  toggle: () => {},
});
export const useTheme = () => useContext(ThemeContext);

// ── SVG Icon System ────────────────────────────────────────────────────────────
function Icon({ name, size = 18, className }: { name: string; size?: number; className?: string }) {
  const paths: Record<string, string> = {
    dashboard:
      'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z',
    users:
      'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
    shield:
      'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
    clipboard:
      'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z',
    'user-circle':
      'M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z',
    tag: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L9.568 3z M6.75 7.5a.75.75 0 100-1.5.75.75 0 000 1.5z',
    bars: 'M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5',
    logout:
      'M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75',
    'chevron-left': 'M15.75 19.5L8.25 12l7.5-7.5',
    'chevron-right': 'M8.25 4.5l7.5 7.5-7.5 7.5',
    cog: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
    x:     'M6 18L18 6M6 6l12 12',
    film:  'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z',
    play:  'M21 12a9 9 0 11-18 0 9 9 0 0118 0z M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z',
    video: 'M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5',
    chart: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={paths[name] ?? ''} />
    </svg>
  );
}

// ── Nav Structure ──────────────────────────────────────────────────────────────
const NAV = [
  // {
  //   group: 'Overview',
  //   items: [
  //     { label: 'Dashboard',             href: '/admin',           icon: 'dashboard', permissions: [] as string[] },
  //     { label: 'Analytics & Reporting', href: '/admin/analytics', icon: 'chart',     permissions: [] as string[] },
  //   ],
  // },
  // {
  //   group: 'Content Studio',
  //   items: [
  //     { label: 'Series',            href: '/admin/series',         icon: 'film',      permissions: [] as string[] },
  //     { label: 'Episodes',          href: '/admin/episodes',       icon: 'play',      permissions: [] as string[] },
  //     { label: 'Video Pipeline',    href: '/admin/video-pipeline', icon: 'cog',       permissions: [] as string[] },
  //     { label: 'Subtitles',         href: '/admin/subtitles',      icon: 'clipboard', permissions: [] as string[] },
  //     { label: 'Artists & Credits', href: '/admin/artists',        icon: 'users',     permissions: [] as string[] },
  //   ],
  // },
  // {
  //   group: 'Discovery',
  //   items: [
  //     { label: 'Home Feed', href: '/admin/home-feed', icon: 'dashboard', permissions: [] as string[] },
  //     { label: 'Banners',   href: '/admin/banners',   icon: 'chart',     permissions: [] as string[] },
  //   ],
  // },
  // {
  //   group: 'Monetisation',
  //   items: [
  //     { label: 'Paywall Rules',  href: '/admin/paywall',        icon: 'shield',    permissions: [] as string[] },
  //     { label: 'Keys Economy',   href: '/admin/keys',           icon: 'chart',     permissions: [] as string[] },
  //     { label: 'Subscriptions',  href: '/admin/subscriptions',  icon: 'cog',       permissions: [] as string[] },
  //   ],
  // },
  // {
  //   group: 'Marketing',
  //   items: [
  //     { label: 'Push Notifications', href: '/admin/notifications',  icon: 'bell',      permissions: [] as string[] },
  //     { label: 'Landing Pages',      href: '/admin/landing-pages',  icon: 'clipboard', permissions: [] as string[] },
  //     { label: 'Campaigns',          href: '/admin/campaigns',      icon: 'chart',     permissions: [] as string[] },
  //   ],
  // },
  // {
  //   group: 'Users & Support',
  //   items: [
  //     { label: 'User Search',      href: '/admin/users',   icon: 'users',     permissions: [] as string[] },
  //     { label: 'Support Tickets',  href: '/admin/support', icon: 'clipboard', permissions: [] as string[] },
  //   ],
  // },
  // {
  //   group: 'Finance',
  //   items: [
  //     { label: 'Transactions',    href: '/admin/transactions',    icon: 'chart',     permissions: [] as string[] },
  //     { label: 'Reconciliation',  href: '/admin/reconciliation',  icon: 'clipboard', permissions: [] as string[] },
  //   ],
  // },
  {
    group: 'Content Studio',
    items: [
      { label: 'Series',            href: '/admin/series',         icon: 'film',        permissions: ['content.series.view'] },
      { label: 'Episodes',          href: '/admin/episodes',       icon: 'play',        permissions: ['content.episodes.view'] },
      { label: 'Genres',            href: '/admin/genres',         icon: 'tag',         permissions: ['content.genres.view'] },
      { label: 'Video Pipeline',    href: '/admin/video-pipeline', icon: 'video',       permissions: ['content.video.view'] },
      // { label: 'Subtitles',         href: '/admin/subtitles',      icon: 'clipboard',   permissions: ['content.subtitles.view'] },
      { label: 'Artists & Credits',    href: '/admin/artists',      icon: 'user-circle', permissions: ['content.artists.view'] },
      // { label: 'Story Submissions',     href: '/admin/submissions',  icon: 'clipboard',   permissions: ['content.stories.view'] },
    ],
  },
  {
    group: 'Discovery',
    items: [
      { label: 'Home Feed', href: '/admin/home-feed', icon: 'bars',  permissions: ['feed.shelves.view'] },
      { label: 'Banners',   href: '/admin/banners',   icon: 'film',  permissions: ['feed.banners.view'] },
    ],
  },
  // {
  //   group: 'Marketing',
  //   items: [
  //     { label: 'Campaigns', href: '/admin/campaigns', icon: 'tag', permissions: ['growth.campaigns.view'] },
  //   ],
  // },
  {
    group: 'Monetisation',
    items: [
      { label: 'Paywall Rules', href: '/admin/paywall', icon: 'shield', permissions: ['commerce.paywall.view'] },
      { label: 'Keys Economy', href: '/admin/keys', icon: 'chart', permissions: ['commerce.keys.view'] },
      { label: 'Subscriptions', href: '/admin/subscriptions', icon: 'cog', permissions: ['commerce.subscriptions.view'] },
    ],
  },
  // {
  //   group: 'Users & Support',
  //   items: [
  //     { label: 'User Search',      href: '/admin/users',   icon: 'users',     permissions: [] as string[] },
  //     { label: 'Support Tickets',  href: '/admin/support', icon: 'clipboard', permissions: [] as string[] },
  //   ],
  // },
  {
    group: 'Finance',
    items: [
      { label: 'Transactions', href: '/admin/transactions', icon: 'chart', permissions: ['payment.transactions.view'] },
      { label: 'Reconciliation', href: '/admin/reconciliation', icon: 'clipboard', permissions: ['payment.reconciliation.view'] },
    ],
  },
  {
    group: 'Security',
    items: [
      { label: 'Admin Users',          href: '/admin/admin-users', icon: 'users',     permissions: ['admin.users.view'] },
      { label: 'Roles & Permissions',  href: '/admin/roles',       icon: 'shield',    permissions: ['admin.permissions.view'] },
    ],
  },
  {
    group: 'Compliance',
    items: [
      { label: 'Audit Logs', href: '/admin/audit-logs', icon: 'clipboard', permissions: ['audit.logs.view'] },
    ],
  },
];

// ── Page title map for topbar ──────────────────────────────────────────────────
const PAGE_TITLE: Record<string, string> = {
  '/admin':                 'Dashboard',
  '/admin/series':          'Series Manager',
  '/admin/episodes':        'Episode Manager',
  '/admin/genres':          'Genres',
  '/admin/video-pipeline':  'Video Pipeline',
  '/admin/subtitles':       'Subtitles',
  '/admin/artists':         'Artists & Credits',
  '/admin/submissions':     'Story Submissions',
  '/admin/home-feed':       'Home Feed',
  '/admin/banners':         'Hero Banners',
  '/admin/campaigns':       'Campaigns',
  '/admin/keys':            'Keys Economy',
  '/admin/subscriptions':   'Subscriptions',
  '/admin/users':           'User Support Console',
  '/admin/support':         'Support',
  '/admin/transactions':    'Transactions',
  '/admin/reconciliation':  'Reconciliation',
  '/admin/admin-users':     'Admin Users',
  '/admin/roles':           'Roles & Permissions',
  '/admin/audit-logs':      'Audit Logs',
  '/admin/settings':        'My Account',
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// ── Sidebar Item ───────────────────────────────────────────────────────────────
function SidebarItem({
  item,
  collapsed,
}: {
  item: { label: string; href: string; icon: string };
  collapsed: boolean;
}) {
  const pathname = usePathname();
  const active =
    pathname === item.href || (item.href !== '/admin' && pathname.startsWith(item.href));
  return (
    <Link
      href={item.href}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 group relative ${
        active
          ? 'bg-[#C3FB15]/10 text-[#C3FB15]'
          : 'text-white/45 hover:text-white/85 hover:bg-white/5'
      }`}
    >
      <Icon
        name={item.icon}
        size={16}
        className={`flex-shrink-0 transition-colors ${
          active ? 'text-[#C3FB15]' : 'text-white/25 group-hover:text-white/55'
        }`}
      />
      {!collapsed && (
        <span className="truncate text-[13px] font-medium leading-none">{item.label}</span>
      )}
      {active && !collapsed && (
        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#C3FB15] flex-shrink-0" />
      )}
      {collapsed && (
        <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-[#1C1C1C] border border-white/10 rounded-lg text-xs text-white/85 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity shadow-2xl">
          {item.label}
        </div>
      )}
    </Link>
  );
}

// ── Auth Loading Screen ────────────────────────────────────────────────────────
function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-white/10 border-t-[#C3FB15] rounded-full animate-spin" />
        <span className="text-xs text-white/30">Loading…</span>
      </div>
    </div>
  );
}

// ── Admin Layout ───────────────────────────────────────────────────────────────
export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [dark, setDark] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const { admin, isAuthenticated, isInitialized, logout, requiresMfa } = useAuth();
  const { canSeeSection, getVisibleItems } = useNavAccess();
  const setPermissions = useAuthStore(s => s.setPermissions);

  const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated || admin?.role === 'super_admin') return;
    try {
      const data = await apiClient.get<never, { permissions: string[] }>(AUTH_ENDPOINTS.MY_PERMISSIONS);
      setPermissions(data.permissions);
    } catch {
      // Non-fatal — stale permissions stay in place until next successful fetch.
    }
  }, [isAuthenticated, admin?.role, setPermissions]);

  useEffect(() => {
    refreshPermissions();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('focus', refreshPermissions);
    return () => window.removeEventListener('focus', refreshPermissions);
  }, [refreshPermissions]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const isLoginPage = pathname === '/admin/login';
  const isMfaSetupPage = pathname === '/admin/mfa-setup';
  const isStandalonePage =
    isLoginPage ||
    isMfaSetupPage ||
    pathname === '/admin/forgot-password' ||
    pathname === '/admin/reset-password' ||
    pathname === '/admin/accept-invite';

  const mfaSetupRequired =
    isAuthenticated && admin !== null && requiresMfa(admin.role) && !admin.mfa_enabled;

  useEffect(() => {
    if (!isStandalonePage && isInitialized && !isAuthenticated) {
      router.push('/admin/login');
    }
    if (isInitialized && isAuthenticated && mfaSetupRequired && !isMfaSetupPage) {
      router.push('/admin/mfa-setup');
    }
  }, [isAuthenticated, isInitialized, isStandalonePage, isMfaSetupPage, mfaSetupRequired, router]);

  if (isStandalonePage) return <>{children}</>;
  if (!isInitialized || !isAuthenticated || mfaSetupRequired) return <AuthLoadingScreen />;

  const handleLogout = async () => {
    await logout();
    router.push('/admin/login');
  };

  const visibleNav = NAV
    .filter(section => canSeeSection(section))
    .map(section => ({ ...section, items: getVisibleItems(section) }));

  const initials = admin ? getInitials(admin.name) : '??';
  const roleLabel = admin ? (ROLE_LABELS[admin.role] ?? admin.role) : '';
  const pageTitle = PAGE_TITLE[pathname] ?? '';

  return (
    <ThemeContext.Provider value={{ dark, toggle: () => setDark(d => !d) }}>
      <div className="min-h-screen bg-[#0A0A0A] text-white flex">

        {/* ── Mobile overlay ── */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/65 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* ── Sidebar ── */}
        <aside
          className={`fixed top-0 left-0 h-full z-50 flex flex-col bg-[#0D0D0D] border-r border-white/[0.05] transition-all duration-300 ${
            collapsed ? 'w-[70px]' : 'w-[240px]'
          } ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        >
          {/* Logo */}
          <div className="flex items-center gap-3 px-4 h-14 border-b border-white/[0.05] flex-shrink-0">
            <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
              <Image
                src="/assets/images/Logo walktalkMain/Logo walktalk.svg"
                alt="WalkTalk Studios"
                width={32}
                height={32}
                className="object-contain"
              />
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-[13px] font-semibold leading-none tracking-tight truncate">
                  WalkTalk Studios
                </div>
                <div className="text-[10px] text-white/30 leading-none mt-0.5">Admin Console</div>
              </div>
            )}
          </div>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
            {visibleNav.map(section => (
              <div key={section.group}>
                {!collapsed ? (
                  <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/18 px-3 mb-2">
                    {section.group}
                  </div>
                ) : (
                  <div className="w-6 h-px bg-white/[0.06] mx-auto mb-2" />
                )}
                <div className="space-y-0.5">
                  {section.items.map(item => (
                    <SidebarItem key={item.href} item={item} collapsed={collapsed} />
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Sidebar footer */}
          <div className="border-t border-white/[0.05] flex-shrink-0 p-2 space-y-1">
            {/* User profile mini-card */}
            <Link
              href="/admin/settings"
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white/5 transition-all group relative ${
                pathname === '/admin/settings' ? 'bg-[#C3FB15]/8 text-[#C3FB15]' : 'text-white/55 hover:text-white/85'
              }`}
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 transition-all ${
                  pathname === '/admin/settings'
                    ? 'bg-[#C3FB15] text-[#0A0A0A]'
                    : 'bg-white/10 text-white/65 group-hover:bg-[#C3FB15]/15 group-hover:text-[#C3FB15]'
                }`}
              >
                {initials}
              </div>
              {!collapsed && (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium truncate leading-none text-white/75 group-hover:text-white/90">
                      {admin?.name ?? '—'}
                    </div>
                    <div className="text-[10px] text-white/30 truncate mt-0.5">{roleLabel}</div>
                  </div>
                  <Icon
                    name="cog"
                    size={13}
                    className="flex-shrink-0 text-white/18 group-hover:text-white/45"
                  />
                </>
              )}
              {collapsed && (
                <div className="absolute left-full ml-3 px-2.5 py-1.5 bg-[#1C1C1C] border border-white/10 rounded-lg text-xs text-white/85 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity shadow-2xl">
                  My Account
                </div>
              )}
            </Link>

            {/* Collapse toggle */}
            <button
              onClick={() => setCollapsed(c => !c)}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-white/22 hover:text-white/55 hover:bg-white/5 transition-all"
            >
              <Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={13} />
              {!collapsed && <span className="text-[11px]">Collapse</span>}
            </button>
          </div>
        </aside>

        {/* ── Main content area ── */}
        <div
          className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ${
            collapsed ? 'lg:ml-[70px]' : 'lg:ml-[240px]'
          }`}
        >
          {/* ── Topbar / Header ── */}
          <header className="sticky top-0 z-30 h-14 flex items-center gap-3 px-4 border-b border-white/[0.05] bg-[#0D0D0D]/90 backdrop-blur-md flex-shrink-0">

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-1.5 rounded-lg text-white/40 hover:text-white/75 hover:bg-white/8 transition-all flex-shrink-0"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Icon name="bars" size={18} />
            </button>

            {/* Page title */}
            {pageTitle && (
              <div className="hidden lg:flex items-center gap-2">
                <h1 className="text-[13px] font-semibold text-white/70">{pageTitle}</h1>
              </div>
            )}

            {/* Right side */}
            <div className="flex items-center gap-1.5 ml-auto">

              {/* Profile */}
              <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-white/[0.05] bg-white/[0.02]">
                <Link
                  href="/admin/settings"
                  className="w-6 h-6 rounded-full bg-[#C3FB15] flex items-center justify-center text-[10px] font-bold text-[#0A0A0A] hover:ring-2 hover:ring-[#C3FB15]/40 transition-all flex-shrink-0"
                  title="Account settings"
                >
                  {initials}
                </Link>
                <div className="hidden sm:block min-w-0">
                  <div className="text-[12px] font-medium leading-none truncate max-w-[130px] text-white/80">
                    {admin?.name ?? '—'}
                  </div>
                  <div className="text-[10px] text-white/30 leading-none mt-0.5 truncate">{roleLabel}</div>
                </div>
              </div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                title="Sign out"
                className="p-2 rounded-lg text-white/28 hover:text-red-400 hover:bg-red-500/10 transition-all"
              >
                <Icon name="logout" size={15} />
              </button>
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-auto">{children}</main>
        </div>
      </div>
    </ThemeContext.Provider>
  );
}
